"use client";

import { useCallback, type ReactNode } from "react";
import Image from "next/image";
import Link from "next/link";
import { Icon } from "./profile/icon";

import { PhoneReveal } from "@/components/phone-reveal";

export type AgentContactSurface = "property_agent" | "contact_rail";

export interface AgentContactCardProps {
  agentId: string;
  name: string;
  photoUrl: string | null;
  phone: string | null;
  brokerageName: string | null;
  brokerageAddress?: string | null;
  brokeragePhone?: string | null;
  licenseNumber: string | null;
  licenseState?: string | null;
  profileHref?: string | null;
  showProfileLink?: boolean;
  surface: AgentContactSurface;
  heading?: string;
  actions?: ReactNode;
  children?: ReactNode;
}

function initialsFor(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0]!.charAt(0).toUpperCase();
  return `${words[0]!.charAt(0)}${words.at(-1)!.charAt(0)}`.toUpperCase();
}

/** Shared contact-card presentation for property details and agent profiles. */
export function AgentContactCard({
  agentId,
  name,
  photoUrl,
  phone,
  brokerageName,
  brokerageAddress = null,
  brokeragePhone = null,
  licenseNumber,
  licenseState = null,
  profileHref = null,
  showProfileLink = true,
  surface,
  heading = "Agent Contact",
  actions,
  children,
}: AgentContactCardProps) {
  const onReveal = useCallback(() => {
    void fetch("/api/agents/reveal-phone", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ agentId, surface }),
    }).catch(() => {});
  }, [agentId, surface]);

  return (
    <section
      aria-label={heading}
      className="flex flex-col gap-4 rounded-lg border p-5"
      style={{
        borderColor: "var(--brand-card-border)",
        borderRadius: "var(--radius-card, 8px)",
        boxShadow: "var(--shadow-resting)",
        backgroundColor: "var(--brand-card-bg)",
      }}
    >
      <h2
        className="text-xs font-semibold uppercase"
        style={{ color: "var(--brand-text-muted)", letterSpacing: "0.08em" }}
      >
        {heading}
      </h2>

      <div className="flex items-center gap-3">
        <span
          className="relative inline-flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-lg font-semibold"
          style={{
            backgroundColor: "var(--brand-surface-subtle)",
            color: "var(--brand-primary)",
            fontSize: "18px",
          }}
        >
          {photoUrl ? (
            <Image
              src={photoUrl}
              alt={name}
              fill
              sizes="56px"
              unoptimized
              className="object-cover"
            />
          ) : (
            initialsFor(name)
          )}
        </span>
        <div className="min-w-0">
          <p
            className="truncate font-semibold"
            style={{ color: "var(--brand-text)", fontSize: "16px" }}
          >
            {name}
          </p>
          {brokerageName && (
            <p
              className="truncate"
              style={{ color: "var(--brand-text-muted)", fontSize: "13px" }}
            >
              {brokerageName}
            </p>
          )}
          {licenseNumber && (
            <p style={{ color: "var(--brand-text-muted)", fontSize: "12px" }}>
              License: {licenseNumber}
              {licenseState ? ` (${licenseState})` : ""}
            </p>
          )}
        </div>
      </div>

      {(brokerageAddress || brokeragePhone) && (
        <div
          className="space-y-2 border-t pt-3 text-sm"
          style={{
            borderColor: "var(--brand-card-border)",
            color: "var(--brand-text-muted)",
          }}
        >
          {brokerageAddress && (
            <p className="flex items-start gap-2">
              <Icon name="location_on" size={17} className="mt-px shrink-0" />
              <span>{brokerageAddress}</span>
            </p>
          )}
          {brokeragePhone && (
            <p className="flex items-start gap-2">
              <Icon name="call" size={17} className="mt-px shrink-0" />
              <span>{brokeragePhone}</span>
            </p>
          )}
        </div>
      )}

      {(phone || (showProfileLink && profileHref)) && (
        <div className="flex flex-col gap-2">
          {phone && (
            <PhoneReveal
              phone={phone}
              onReveal={onReveal}
              size="md"
              variant="button"
            />
          )}
          {showProfileLink && profileHref && (
            <Link
              href={profileHref}
              className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-md border px-4 py-2.5 font-semibold"
              style={{
                borderColor: "var(--brand-card-border)",
                backgroundColor: "var(--brand-card-bg)",
                color: "var(--brand-text)",
                borderRadius: "var(--radius-button, 6px)",
                fontSize: "14px",
              }}
            >
              <Icon name="person" size={18} />
              View profile
            </Link>
          )}
        </div>
      )}
      {actions}
      {children}
    </section>
  );
}
